/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  FormLabel,
  SelectV2,
  Spacer,
  TextInput,
} from '@smartsheet/lodestar-core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  selectRunOptions,
  selectUnitOptions,
  Option,
} from '../../../../../../../services/ducks/schedulesDuck';
import {
  isDurationValid,
  locale,
  ScheduleStateSelectType,
  ScheduleStateStringType,
  getValidationState,
} from './utils';
const columnWidth = css`
  width: 50%;
`;

const customInputsCntainer = css`
  align-items: flex-start;
`;

export const Frequency = ({
  setRun,
  setDuration,
  setUnit,
  run,
  duration,
  unit,
  isRequired,
}: {
  run: ScheduleStateSelectType;
  duration: ScheduleStateStringType;
  unit: ScheduleStateSelectType;
  setRun: (val: ScheduleStateSelectType) => void;
  setDuration: (val: ScheduleStateStringType) => void;
  setUnit: (val: ScheduleStateSelectType) => void;
  isRequired?: boolean;
}) => {
  const { t: translate } = useTranslation();
  const RUN_OPTIONS = useSelector(selectRunOptions);

  const UNIT_OPTIONS = useSelector(selectUnitOptions);

  const { value: runValue, valid: runValid } = run;
  const { value: durationValue, valid: durationValid } = duration;
  const { value: unitValue, valid: unitValid } = unit;

  const shouldCustomRunOptionsBeVisible = runValue?.value === 'Custom';

  const [hasRunChanged, setHasRunChanged] = useState(false);

  const runChangeHandler = (run: Option | null) => {
    setHasRunChanged(true);
    setRun({
      value: run,
      valid: true,
    });
  };

  const runValidState = hasRunChanged ? runValid : true;

  const [durationValidationMessage, setDurationValidationMessage] =
    useState<string>(
      translate('designer:triggerPanel:schedules:upsert:required')
    );

  const durationChangeHandler = (duration: string, unit: Option | null) => {
    const err: string | undefined = isDurationValid(duration, unit);
    if (err) {
      setDurationValidationMessage(err);
    }
    setDuration({ value: duration, valid: err === undefined });
  };

  const unitChangeHandler = (unit: Option | null) => {
    if (unit) {
      setUnit({
        value: unit,
        valid: isRequired ? unit?.value.length > 0 : true,
      });
      durationChangeHandler(durationValue, unit);
    }
  };

  return (
    <React.Fragment>
      <FormLabel
        validationState="info"
        validationMessage={translate(
          'designer:triggerPanel:schedules:upsert:runTimeNotice'
        )}
        label={translate('designer:triggerPanel:schedules:upsert:run')}
      >
        {labelProps => (
          <SelectV2
            {...labelProps}
            locale={locale}
            options={RUN_OPTIONS}
            selectedOption={runValue}
            onChange={runChangeHandler}
            getOptionKey={(o: Option) => o.label}
            isClearable={false}
            isSearchable={true}
            validationState={getValidationState(runValidState)}
          />
        )}
      </FormLabel>
      {shouldCustomRunOptionsBeVisible ? (
        <Spacer orientation="row" space="small" css={customInputsCntainer}>
          <FormLabel
            label={translate('designer:triggerPanel:schedules:every')}
            labelProps={{ lang: locale }}
            descriptionProps={{ lang: locale }}
            validationState={getValidationState(durationValid)}
            validationMessage={durationValidationMessage}
            validationProps={{ 'aria-haspopup': 'false' }}
            css={columnWidth}
          >
            {inputProps => (
              <TextInput
                onChange={value => durationChangeHandler(value, unitValue)}
                value={durationValue || ''}
                type="number"
                min={unitValue?.value === 'minutes' ? '15' : '1'}
                step={unitValue?.value === 'minutes' ? '15' : '1'}
                validationState={getValidationState(durationValid)}
                {...inputProps}
              />
            )}
          </FormLabel>
          <FormLabel
            label={translate('designer:triggerPanel:schedules:upsert:unit')}
            validationState={getValidationState(unitValid)}
            css={columnWidth}
          >
            {labelProps => (
              <SelectV2
                {...labelProps}
                locale={locale}
                options={UNIT_OPTIONS}
                selectedOption={unitValue}
                onChange={unitChangeHandler}
                getOptionKey={(o: Option) => o.label}
                isClearable={false}
                isSearchable={true}
                validationState={getValidationState(unitValid)}
              />
            )}
          </FormLabel>
        </Spacer>
      ) : null}
    </React.Fragment>
  );
};
